/**
 * Snap-to-align utilities for canvas element positioning
 */

export interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapGuides {
  vertical: number[];   // x positions of vertical guide lines
  horizontal: number[]; // y positions of horizontal guide lines
}

export interface SnapResult {
  x: number;
  y: number;
  guides: SnapGuides;
}

interface SnapTarget {
  position: number;
  type: 'left' | 'right' | 'centerX' | 'top' | 'bottom' | 'centerY';
}

const DEFAULT_THRESHOLD = 5;

/**
 * Calculate snap points for an element (edges + center)
 */
function getElementSnapPoints(bounds: ElementBounds): { x: SnapTarget[]; y: SnapTarget[] } {
  const left = bounds.x;
  const right = bounds.x + bounds.width;
  const centerX = bounds.x + bounds.width / 2;
  const top = bounds.y;
  const bottom = bounds.y + bounds.height;
  const centerY = bounds.y + bounds.height / 2;

  return {
    x: [
      { position: left, type: 'left' },
      { position: right, type: 'right' },
      { position: centerX, type: 'centerX' },
    ],
    y: [
      { position: top, type: 'top' },
      { position: bottom, type: 'bottom' },
      { position: centerY, type: 'centerY' },
    ],
  };
}

/**
 * Get all snap targets from canvas and other elements
 */
function getSnapTargets(
  otherElements: ElementBounds[],
  canvasSize: { width: number; height: number }
): { x: number[]; y: number[] } {
  const xTargets: Set<number> = new Set();
  const yTargets: Set<number> = new Set();

  // Canvas edges and center
  xTargets.add(0);                          // left edge
  xTargets.add(canvasSize.width);           // right edge
  xTargets.add(canvasSize.width / 2);       // center

  yTargets.add(0);                          // top edge
  yTargets.add(canvasSize.height);          // bottom edge
  yTargets.add(canvasSize.height / 2);      // center

  // Other elements' edges and centers
  for (const el of otherElements) {
    const points = getElementSnapPoints(el);
    for (const p of points.x) xTargets.add(p.position);
    for (const p of points.y) yTargets.add(p.position);
  }

  return {
    x: Array.from(xTargets),
    y: Array.from(yTargets),
  };
}

/**
 * Find the best snap for a given position against targets
 */
function findSnap(
  elementPoints: number[],
  targets: number[],
  threshold: number
): { snappedOffset: number; guidePosition: number } | null {
  let bestSnap: { snappedOffset: number; guidePosition: number } | null = null;
  let bestDistance = threshold + 1;

  for (const point of elementPoints) {
    for (const target of targets) {
      const distance = Math.abs(point - target);
      if (distance <= threshold && distance < bestDistance) {
        bestDistance = distance;
        bestSnap = {
          snappedOffset: target - point,
          guidePosition: target,
        };
      }
    }
  }

  return bestSnap;
}

/**
 * Calculate snapped position for an element being dragged
 *
 * @param draggingElement - The element being dragged (with proposed new position)
 * @param otherElements - All other elements on the canvas
 * @param canvasSize - The canvas dimensions
 * @param threshold - Snap threshold in pixels (default 5)
 * @returns The snapped position and active guide lines
 */
export function calculateSnap(
  draggingElement: ElementBounds,
  otherElements: ElementBounds[],
  canvasSize: { width: number; height: number },
  threshold: number = DEFAULT_THRESHOLD
): SnapResult {
  const targets = getSnapTargets(otherElements, canvasSize);
  const elementPoints = getElementSnapPoints(draggingElement);

  // Get x positions of dragging element (left, right, center)
  const xPoints = elementPoints.x.map(p => p.position);
  const yPoints = elementPoints.y.map(p => p.position);

  // Find best snaps
  const xSnap = findSnap(xPoints, targets.x, threshold);
  const ySnap = findSnap(yPoints, targets.y, threshold);

  // Calculate snapped position
  const snappedX = xSnap ? draggingElement.x + xSnap.snappedOffset : draggingElement.x;
  const snappedY = ySnap ? draggingElement.y + ySnap.snappedOffset : draggingElement.y;

  // Collect active guide lines
  const guides: SnapGuides = {
    vertical: xSnap ? [xSnap.guidePosition] : [],
    horizontal: ySnap ? [ySnap.guidePosition] : [],
  };

  return {
    x: snappedX,
    y: snappedY,
    guides,
  };
}
